/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-07-11
 */

import React, { useEffect, useState } from "react";
import { LeafTreeOfLife } from "../components/LeafTreeOfLife";
import { LeafForm } from "../components/LeafForm";
import { Link, useParams } from "react-router-dom";
import { debug, fetchRecords, scrollTop } from "../utils/AppUtil";
import { Helmet } from "react-helmet";

type DeceasedType = {
    deceasedId: string,
    title: string,
    firstName: string,
    lastName: string,
    middleName: string,
    hebrewName: string,
    tribe: string,
    gender: string,
    birthDate: string,
    birthDateDisplay: string,
    deathDate: string,
    deathDateDisplay: string,
    burialDateDisplay: string,
    burialTimeDisplay: string,
    consecrationDateDisplay: string,
    consecrationTimeDisplay: string,
    yahrzeitDateDisplay: string,
    yahrzeitDesc: string,
    cemeteryName: string,
    synagogueName: string,
    synagogueEmail: string,
    locationDesc: string,
    locationNumber: string,
    ashesDesc: string,
    metaTagTitle: string,
    metaTagDescription: string,
    metaTagKeywords: string
}

export const TreeOfLife: React.FC = () => {

    type DeceasedParamsType = { deceasedId: string, receiptId: string };
    const params: DeceasedParamsType = useParams();
    const endpoint = `deceased/${params.deceasedId}`;

    const deceasedData: DeceasedType = {
        deceasedId: "",
        title: "",
        firstName: "",
        lastName: "",
        middleName: "",
        hebrewName: "",
        tribe: "",
        gender: "",
        birthDate: "",
        birthDateDisplay: "",
        deathDate: "",
        deathDateDisplay: "",
        burialDateDisplay: "",
        burialTimeDisplay: "",
        consecrationDateDisplay: "",
        consecrationTimeDisplay: "",
        yahrzeitDateDisplay: "",
        yahrzeitDesc: "",
        cemeteryName: "",
        synagogueName: "",
        synagogueEmail: "",
        locationDesc: "",
        locationNumber: "",
        ashesDesc: "",
        metaTagTitle: "",
        metaTagDescription: "",
        metaTagKeywords: ""
    }
    const [deceased, setDeceased] = useState<DeceasedType>(deceasedData);
    const processData = (responseJson: DeceasedType) => {
        setDeceased(responseJson);
    }

    // Do not change this or put any additional code in here without checking with
    // the team first.
    useEffect(() => {

        fetchRecords(endpoint, processData)
        .then(() => {scrollTop()})
        .catch((error) => {
            console.log("Error : " + error)
        });

    }, [endpoint]);

    return(
        <>
            <Helmet>
                <title>Buy a Leaf - Jewish Cemetery</title>
                <meta name="description" content="This website was designed by the Jewish Cemeteries Maintenance Board to pay tribute to loved ones that are no longer with us." />
            </Helmet>
        
            <div className="jc-grid mx-auto" style={{maxWidth: "1028px"}}>
                <p className="jc-txt-body fs-6 mb-4"><b><Link to={'/deceased/' + params.deceasedId} className="jc-txt-dark">{deceased.firstName} {deceased.lastName}</Link></b>&nbsp;&nbsp;&nbsp;&gt;&nbsp;&nbsp;&nbsp;Tree of Life</p>

                <div className="row">

                    <div className="col-md-6">
                        <p className="jc-txt-heading fs-3 mb-3">Buy a Leaf for {deceased.firstName}</p>
                        <div>
                            <LeafForm endpoint={"leaf"} receiptId={params.receiptId} deceasedId={params.deceasedId} firstName={deceased.firstName} lastName={deceased.lastName} />
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="d-flex justify-content-center pb-3">
                            <img src="/images/leaf-modal.png" />
                        </div>
                        <p className="jc-txt-body fw-6 pb-3">
                            Leaves are virtual representations of our love for the deceased. The proceeds from leaves purchased will go towards cemetery maintenance.
                        </p>

                        <LeafTreeOfLife endpoint={"leaf/deceased"} deceasedId={params.deceasedId} />
                    </div>

                </div>
            </div>
        </>
    );
}